import { useEquivoltRoles } from "@/app/equivolt-app/shared/composables/useEquivoltRoles";
import {
  getDefaultHomeManagerRoute,
  getDefaultHomePreSalesManagerRoute,
  getDefaultHomeUserRoute,
} from "@/app/equivolt-app/shared/helper/getDefaultHomeRoute.helper";
import { AppRouteNames } from "@/shared/@types/AppRouteNames";
import { FeatureFlag } from "@/shared/@types/FeatureFlag";
import { RoleActions } from "@/shared/@types/RoleActions";
import { usePermission } from "@/shared/composables/usePermission.composable";
import { useFeatureFlagsStore } from "@/shared/stores/FeatureFlagsStore";
import type { RouteLocationNormalizedLoaded, RouteRecordRaw } from "vue-router";

const StationsView = () =>
  import("@/app/equivolt-app/fleet-at-home/stations/StationsView/StationsView.vue");
const DashboardView = () =>
  import("@/app/equivolt-app/fleet-at-home/dashboard/DashboardView/DashboardView.vue");
const ChargesView = () =>
  import("@/app/equivolt-app/fleet-at-home/charges/ChargesView/ChargesView.vue");
const CreditsView = () =>
  import("@/app/equivolt-app/fleet-at-home/credits/CreditsView/CreditsView.vue");
const ShopView = () =>
  import("@/app/equivolt-app/fleet-at-home/shop/ShopView/ShopView.vue");
const ShopProductView = () =>
  import("@/app/equivolt-app/fleet-at-home/shop/product/ProductView/ProductView.vue");
const ShopCatalogueProductView = () =>
  import("@/app/equivolt-app/fleet-at-home/shop/catalogue-product/CatalogueProductView/CatalogueProductView.vue");
const ManagementDashboardView = () =>
  import("@/app/equivolt-app/fleet-at-home-management/dashboard/DashboardView/DashboardView.vue");
const ManagementChargesView = () =>
  import("@/app/equivolt-app/fleet-at-home-management/charges/ChargesView/ChargesView.vue");
const ManagementUsersView = () =>
  import("@/app/equivolt-app/fleet-at-home-management/users/UsersView/UsersView.vue");
const ManagementTcoCalculatorView = () =>
  import("@/app/equivolt-app/fleet-at-home-management/tco-calculator/TcoCalculatorView/TcoCalculatorView.vue");
const ManagementSettingsView = () =>
  import("@/app/equivolt-app/fleet-at-home-management/settings/SettingsView/SettingsView.vue");
const PageNotFoundView = () =>
  import("@/shared/views/PageNotFoundView/PageNotFoundView.vue");

const DefaultEntryView = { render: () => null };
const isDashboardEnabled = () =>
  useFeatureFlagsStore().isFeatureEnabled(FeatureFlag.EquivoltKpiDashboards);
const isShopCatalogueProductsEnabled = () =>
  useFeatureFlagsStore().isFeatureEnabled(
    FeatureFlag.EquivoltShopCatalogueProducts,
  );

export const routes: RouteRecordRaw[] = [
  {
    path: "",
    component: DefaultEntryView,
    beforeEnter: async () => ({
      name: getDefaultHomeUserRoute(isDashboardEnabled()),
    }),
  },
  {
    path: "home",
    beforeEnter: () => {
      const { isHomeManager, isHomeUser, isPreSalesManager } =
        useEquivoltRoles();

      if (isHomeUser.value) {
        return true;
      }

      if (isHomeManager.value) {
        return { name: getDefaultHomeManagerRoute(isDashboardEnabled()) };
      }

      return isPreSalesManager.value
        ? { name: getDefaultHomePreSalesManagerRoute() }
        : { name: AppRouteNames.NotFound };
    },
    children: [
      {
        path: "",
        component: DefaultEntryView,
        beforeEnter: async () => ({
          name: getDefaultHomeUserRoute(isDashboardEnabled()),
        }),
      },
      {
        path: "dashboard",
        name: AppRouteNames.FleetAtHomeDashboard,
        meta: {
          titleKey: "equivolt.fleetAtHome.dashboard.title",
        },
        component: DashboardView,
      },
      {
        path: "stations",
        name: AppRouteNames.FleetAtHomeStations,
        meta: {
          titleKey: "equivolt.fleetAtHome.stations.title",
        },
        component: StationsView,
      },
      {
        path: "charges",
        name: AppRouteNames.FleetAtHomeCharges,
        meta: {
          titleKey: "equivolt.fleetAtHome.charges.title",
        },
        props: (route: RouteLocationNormalizedLoaded) => ({
          chargeQueryId: route.query.chargeId,
        }),
        component: ChargesView,
      },
      {
        path: "credits",
        name: AppRouteNames.FleetAtHomeCredits,
        meta: {
          titleKey: "equivolt.fleetAtHome.credits.title",
        },
        component: CreditsView,
      },
      {
        path: "shop",
        name: AppRouteNames.FleetAtHomeShop,
        meta: {
          titleKey: "equivolt.fleetAtHome.shop.title",
        },
        component: ShopView,
      },
      {
        path: "shop/:productId",
        name: AppRouteNames.FleetAtHomeShopProduct,
        component: ShopProductView,
        props: true,
      },
      {
        path: "shop/:productExternalId/:productVariantExternalId",
        name: AppRouteNames.FleetAtHomeShopProductVariant,
        beforeEnter: () =>
          isShopCatalogueProductsEnabled()
            ? true
            : { name: AppRouteNames.FleetAtHomeNotFound },
        component: ShopCatalogueProductView,
        props: true,
      },
    ],
  },
  {
    path: "home-management",
    beforeEnter: () => {
      const { isHomeManager, isHomeUser, isPreSalesManager } =
        useEquivoltRoles();

      if (isHomeManager.value || isPreSalesManager.value) {
        return true;
      }

      return isHomeUser.value
        ? { name: AppRouteNames.FleetAtHomeStations }
        : { name: AppRouteNames.NotFound };
    },
    children: [
      {
        path: "",
        component: DefaultEntryView,
        beforeEnter: async () => {
          const { isHomeManager, isPreSalesManager } = useEquivoltRoles();

          if (isHomeManager.value) {
            return {
              name: getDefaultHomeManagerRoute(isDashboardEnabled()),
            };
          }

          return isPreSalesManager.value
            ? { name: getDefaultHomePreSalesManagerRoute() }
            : { name: AppRouteNames.NotFound };
        },
      },
      {
        path: "dashboard",
        name: AppRouteNames.FleetAtHomeManagementDashboard,
        meta: {
          titleKey: "equivolt.fleetAtHomeManagement.dashboard.title",
        },
        component: ManagementDashboardView,
      },
      {
        path: "users",
        name: AppRouteNames.FleetAtHomeManagementUsers,
        meta: {
          titleKey: "equivolt.fleetAtHomeManagement.users.title",
        },
        props: (route: RouteLocationNormalizedLoaded) => ({
          userQueryId: route.query.userId,
        }),
        component: ManagementUsersView,
      },
      {
        path: "charges",
        name: AppRouteNames.FleetAtHomeManagementCharges,
        meta: {
          titleKey: "equivolt.fleetAtHomeManagement.charges.title",
        },
        component: ManagementChargesView,
      },
      {
        path: "tco-calculator",
        name: AppRouteNames.FleetAtHomeManagementTcoCalculator,
        meta: {
          titleKey: "equivolt.fleetAtHomeManagement.tcoCalculator.title",
        },

        beforeEnter: () => {
          const { hasPermission } = usePermission();

          if (hasPermission(RoleActions.ACCESS_EQUIVOLT_TCO_CALCULATOR)) {
            return true;
          }

          return { name: AppRouteNames.FleetAtHomeNotFound };
        },
        component: ManagementTcoCalculatorView,
      },
      {
        path: "settings",
        name: AppRouteNames.FleetAtHomeManagementSettings,
        meta: {
          titleKey: "equivolt.fleetAtHomeManagement.settings.title",
        },
        component: ManagementSettingsView,
      },
    ],
  },
  {
    path: ":pathMatch(.*)*",
    name: AppRouteNames.FleetAtHomeNotFound,
    meta: {
      titleKey: "shared.error.page.notFoundError.title",
    },
    component: PageNotFoundView,
    props: {
      homeRouteName: AppRouteNames.FleetAtHomeStations,
    },
  },
];
